import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export const ARENA_STATS_RING_SIZE = 84;
export const ARENA_STATS_RING_SIZE_COMPACT = 68;
export const ARENA_STATS_RING_SIZE_NARROW = 56;
export const ARENA_STATS_COMPACT_BREAKPOINT = 400;
export const ARENA_STATS_NARROW_BREAKPOINT = 340;

export function getArenaStatsBannerRingSize(windowWidth: number): number {
  if (windowWidth < ARENA_STATS_NARROW_BREAKPOINT) return ARENA_STATS_RING_SIZE_NARROW;
  if (windowWidth < ARENA_STATS_COMPACT_BREAKPOINT) return ARENA_STATS_RING_SIZE_COMPACT;
  return ARENA_STATS_RING_SIZE;
}

/** Light UI uses a rich purple scoreboard strip; dark UI uses surface depth. */
export function getBannerGradientColors(theme: Theme): [string, string, string] {
  const lightCards = theme.background.card.toUpperCase() === '#FFFFFF';
  if (lightCards) {
    return [theme.primaryDark, theme.primary, theme.primaryDark];
  }
  return [theme.background.primary, theme.background.card, theme.background.primary];
}

export function getBannerTextureColors(theme: Theme): [string, string, string] {
  return ['transparent', `${theme.white}08`, 'transparent'];
}

export function getBannerOrbColors(theme: Theme): [string, string] {
  return [`${theme.primaryLight}40`, 'transparent'];
}

/** SVG ring track — subtle on dark banner */
export function getArenaRingTrackStroke(theme: Theme): string {
  return `${theme.white}14`;
}

export function getArenaWdlRingStrokes(theme: Theme): {
  wins: string;
  draws: string;
  losses: string;
} {
  return {
    wins: theme.success,
    draws: theme.gray[500],
    losses: theme.error,
  };
}

export const getArenaStatsBannerStyles = (theme: Theme, ringSize: number) => {
  const narrow = ringSize <= ARENA_STATS_RING_SIZE_NARROW;
  const compact = ringSize <= ARENA_STATS_RING_SIZE_COMPACT;
  const lightCards = theme.background.card.toUpperCase() === '#FFFFFF';
  const skeletonBg = lightCards ? theme.primaryDark : theme.background.primary;

  return StyleSheet.create({
    bannerWrapper: {
      borderRadius: 20,
      overflow: 'hidden',
      marginBottom: 10,
      marginHorizontal: 4,
    },
    gradientFill: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    textureOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    glowOrb: {
      position: 'absolute',
      width: 160,
      height: 160,
      borderRadius: 80,
      top: -40,
      right: -40,
    },
    bannerContent: {
      paddingHorizontal: narrow ? 10 : compact ? 12 : 14,
      paddingTop: narrow ? 10 : compact ? 12 : 14,
      paddingBottom: narrow ? 10 : compact ? 12 : 12,
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: narrow ? 8 : 10,
    },
    topLeft: {
      gap: 2,
    },
    seasonLabel: {
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 1.4,
      color: `${theme.white}CC`,
      textTransform: 'uppercase',
    },
    hubTitle: {
      fontSize: narrow ? 15 : 18,
      fontWeight: '800',
      color: theme.white,
    },
    streakBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 8,
      backgroundColor: `${theme.accent}24`,
      borderWidth: 1,
      borderColor: `${theme.accent}66`,
    },
    streakText: {
      fontSize: narrow ? 11 : 13,
      fontWeight: '800',
      color: theme.accent,
    },
    statsRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    /** Ring on left, stats text on right — side by side */
    ringAndStats: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: narrow ? 8 : 12,
      flex: narrow ? 1.25 : 1.45,
    },
    ringWrapper: {
      width: ringSize,
      height: ringSize,
      justifyContent: 'center',
      alignItems: 'center',
    },
    ringSvg: {
      position: 'absolute',
      width: ringSize,
      height: ringSize,
    },
    centerContent: {
      justifyContent: 'center',
      alignItems: 'center',
      pointerEvents: 'none',
    },
    winsCenter: {
      fontSize: narrow ? 18 : compact ? 22 : 26,
      fontWeight: '900',
      color: theme.white,
      letterSpacing: -0.5,
    },
    winsCenterSub: {
      fontSize: 7,
      fontWeight: '700',
      color: `${theme.white}CC`,
      letterSpacing: 0.8,
    },
    statsColumn: {
      flex: 1,
      gap: narrow ? 2 : 3,
    },
    recordLabel: {
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 1.2,
      color: `${theme.white}CC`,
      textTransform: 'uppercase',
    },
    wdlRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: narrow ? 2 : 3,
      flexWrap: 'nowrap',
    },
    winsNumber: {
      fontSize: narrow ? 18 : compact ? 20 : 22,
      fontWeight: '900',
      color: theme.white,
      letterSpacing: -0.5,
    },
    winsLabel: {
      fontSize: narrow ? 10 : 12,
      fontWeight: '600',
      color: `${theme.white}CC`,
    },
    wdlSep: {
      fontSize: narrow ? 10 : 12,
      color: `${theme.white}66`,
    },
    drawsText: {
      fontSize: narrow ? 11 : compact ? 12 : 13,
      fontWeight: '700',
      color: `${theme.white}D9`,
    },
    lossesText: {
      fontSize: narrow ? 11 : compact ? 12 : 13,
      fontWeight: '700',
      color: theme.error,
    },
    winRateText: {
      fontSize: narrow ? 9 : 10,
      fontWeight: '600',
      color: `${theme.white}B8`,
    },
    divider: {
      width: 1,
      alignSelf: 'stretch',
      backgroundColor: `${theme.white}1A`,
      marginHorizontal: narrow ? 7 : compact ? 9 : 11,
    },
    rightSection: {
      flex: narrow ? 0.9 : 0.8,
      gap: narrow ? 4 : 6,
    },
    formLabel: {
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 1.2,
      color: `${theme.white}CC`,
      textTransform: 'uppercase',
    },
    formPills: {
      flexDirection: 'row',
      gap: 4,
      flexWrap: 'wrap',
    },
    formPill: {
      width: narrow ? 20 : 22,
      height: narrow ? 20 : 22,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
    },
    formPillW: {
      backgroundColor: theme.secondaryLight,
    },
    formPillD: {
      backgroundColor: theme.gray[200],
    },
    formPillL: {
      backgroundColor: `${theme.error}28`,
      borderWidth: 1,
      borderColor: `${theme.error}66`,
    },
    formPillText: {
      fontSize: narrow ? 10 : 11,
      fontWeight: '800',
    },
    formPillTextW: {
      color: theme.primaryDark,
    },
    formPillTextD: {
      color: theme.gray[700],
    },
    formPillTextL: {
      color: theme.error,
    },
    formHintText: {
      fontSize: narrow ? 9 : 10,
      fontWeight: '600',
      color: `${theme.white}B8`,
    },
    noRightDataText: {
      fontSize: narrow ? 9 : 10,
      color: `${theme.white}99`,
      fontStyle: 'italic',
    },
    skeletonWrapper: {
      borderRadius: 20,
      overflow: 'hidden',
      marginBottom: 10,
      marginHorizontal: 4,
      height: narrow ? 100 : compact ? 118 : 130,
      backgroundColor: skeletonBg,
    },
    skeletonRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: narrow ? 10 : compact ? 12 : 14,
      paddingVertical: narrow ? 10 : compact ? 12 : 14,
      gap: 12,
    },
    skeletonCircle: {
      width: ringSize,
      height: ringSize,
      borderRadius: ringSize / 2,
      backgroundColor: `${theme.white}17`,
    },
    skeletonLines: {
      flex: 1,
      gap: 7,
    },
    skeletonLine: {
      height: 9,
      borderRadius: 4,
      backgroundColor: `${theme.white}17`,
    },
    skeletonLineShort: {
      height: 9,
      borderRadius: 4,
      backgroundColor: `${theme.white}0F`,
      width: '60%',
    },
    errorWrapper: {
      borderRadius: 20,
      overflow: 'hidden',
      marginBottom: 10,
      marginHorizontal: 4,
      paddingHorizontal: 20,
      paddingVertical: 18,
      backgroundColor: theme.primaryDark,
    },
    errorTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.white,
      marginBottom: 6,
    },
    errorMessage: {
      fontSize: 13,
      color: `${theme.white}CC`,
      marginBottom: 12,
    },
    errorRetryButton: {
      alignSelf: 'flex-start',
      backgroundColor: theme.primary,
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 10,
    },
    errorRetryButtonPressed: {
      opacity: 0.85,
    },
    errorRetryButtonHover: {
      opacity: 0.92,
    },
    errorRetryButtonText: {
      color: theme.white,
      fontSize: 13,
      fontWeight: '700',
    },
  });
};
